// Operator-mode flag (ticket P1-E8-1, closes audit finding X-6 / AC-SHELL-4).
//
// Demo/operator chrome (demo reset, presentation overlay, guided tours, RAPID
// "Reset workspace", role-lens scripts, seeded-data controls) must be invisible
// to normal users. This module owns the single source of truth for that flag:
//
// - Only System Admins (`rbac.ROLE_IDS.SYSTEM_ADMIN`) may enable it (`canEnable`).
// - The flag persists in sessionStorage so it survives route changes/reloads
//   within a browser session but never leaks into another user's session.
// - Entering operator mode is audited: `enable()` POSTs a
//   `shell.operator_mode_enabled` event to `/audit-events` (System Admin-gated
//   server route that writes through the standard audit service).
// - `enabled` is always false for actors who cannot enable it, even if a stale
//   sessionStorage flag survives a persona switch away from System Admin.

import { useCallback, useSyncExternalStore } from 'react';
import { rbac } from '@dha-nexus/shared';
import { useCurrentUser } from '../app/CurrentUserContext.jsx';
import { api } from './apiClient.js';

export const OPERATOR_MODE_STORAGE_KEY = 'dha_prime__operator_mode';

const listeners = new Set();

function readStoredFlag() {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(OPERATOR_MODE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeStoredFlag(value) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(OPERATOR_MODE_STORAGE_KEY, String(Boolean(value)));
  } catch {
    /* sessionStorage unavailable (private mode) — flag lives only in memory below */
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return readStoredFlag();
}

/**
 * Shared operator-mode flag.
 *
 * @returns {{ enabled: boolean, canEnable: boolean, enable: () => Promise<void>, disable: () => void }}
 */
export function useOperatorMode() {
  const { roles } = useCurrentUser();
  const canEnable = Array.isArray(roles) && roles.includes(rbac.ROLE_IDS.SYSTEM_ADMIN);
  const storedEnabled = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  // A stored flag from an earlier System Admin session must not grant operator
  // chrome to a non-admin persona in the same browser session.
  const enabled = canEnable && storedEnabled;

  const enable = useCallback(async () => {
    if (!canEnable) return;
    writeStoredFlag(true);
    try {
      // AC-SHELL-4: entering operator mode is audited server-side.
      await api.post('/audit-events', {
        action: 'shell.operator_mode_enabled',
        targetType: 'shell',
        targetId: 'operator-mode',
      });
    } catch (err) {
      // Do not roll the UI back on audit-write failure, but surface it —
      // silent audit loss would be worse than a noisy console.
      console.warn('[operator-mode] failed to record audit event:', err?.message || err);
    }
  }, [canEnable]);

  const disable = useCallback(() => {
    writeStoredFlag(false);
  }, []);

  return { enabled, canEnable, enable, disable };
}
